import { Close as CloseIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { upperFirst } from "lodash";

import { IconButton } from "@/Components/v2/atoms/Button";
import { Dialog } from "@/Components/v2/atoms/Dialog";
import { Stack } from "@/Components/v2/atoms/Stack";
import { BoldSpan, Typography } from "@/Components/v2/atoms/Typography";
import { LikelihoodMapping } from "@/pages/scenario-builder/types";

import { BCPNode } from "@/pages/scenario-builder/v3/utils/bcp";
import HistoricSpendChangeTables from "./HistoricSpendChangeTables";
import { AllocationStrategyResponse } from "@mutiny-group/sbo-openapi-client";

type HistoricSpendChangeModalProps = {
  open: boolean;
  handleClose: () => void;
  allocationStrategy: AllocationStrategyResponse;
  distinctBCP?: Record<string, BCPNode>;
};

const HistoricSpendChangeModal = ({
  open,
  handleClose,
  allocationStrategy,
  distinctBCP,
}: HistoricSpendChangeModalProps) => {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <Stack direction="column" gap={4} p={5}>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ position: "absolute", right: 10, top: 14, width: 44, height: 44 }}
        >
          <CloseIcon />
        </IconButton>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Change from Historic Spend
          </Typography>
        </Stack>
        <Stack direction="column" gap={2}>
          <Typography variant="body2">
            This score measures how much a scenario changes from your past spending, and affects
            your Scenario&apos;s likelihood. While larger changes make your scenario less
            predictable, they&apos;re a way to find new growth opportunities and gather data that
            can improve future strategies.
          </Typography>
          <Stack direction="column" gap={0.5}>
            <BoldSpan>How is this calculated?</BoldSpan>
            <Typography variant="body2">
              The score is based on the size of the change in your total budget and how much
              spending has been reallocated between channels.
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          p={0.5}
          border={`1px solid ${theme.palette.divider}`}
          borderRadius={1}
        >
          {LikelihoodMapping.map(({ strategyProfile, label, description, style }) => {
            const isSelected = strategyProfile === allocationStrategy.strategyProfile;
            return (
              <Stack
                key={strategyProfile}
                p={2}
                direction="column"
                borderRadius={1}
                gap={0.5}
                {...(isSelected && {
                  border: `1px solid #0009FF`,
                  sx: { backgroundColor: style.backgroundColor },
                })}
              >
                <Typography
                  fontSize={14}
                  fontWeight={600}
                  sx={{
                    color: isSelected ? (style.color ?? "inherit") : "inherit",
                  }}
                >
                  {upperFirst(label)} Change
                </Typography>
                <Typography
                  fontSize={14}
                  sx={{
                    color: isSelected ? (style.color ?? "inherit") : "inherit",
                  }}
                >
                  Returns {description}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
        <HistoricSpendChangeTables
          allocationStrategy={allocationStrategy}
          distinctBCP={distinctBCP}
        />
      </Stack>
    </Dialog>
  );
};

export default HistoricSpendChangeModal;
